import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Confetti, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { loadRehearsalSignals, rehearsalScore } from "@/lib/launchRehearsal";

/**
 * Admin overview · one-line launch readiness summary.
 *
 * Pairs with `<AdminSystemWidget>` as a small, dedicated companion line
 * focused specifically on rehearsal progress (live signals only — does
 * not double-count the manual-walk-through items, which only the human
 * doing the rehearsal can confirm).
 *
 * Tone is intentionally calm: green when every live signal has flipped
 * on, neutral otherwise. Never red — this isn't a system error, just a
 * progress nudge.
 */
export default function AdminLaunchReadinessWidget() {
  const [state, setState] = useState({ loading: true, score: null });

  useEffect(() => {
    let alive = true;
    loadRehearsalSignals()
      .then((s) => { if (alive) setState({ loading: false, score: rehearsalScore(s) }); })
      .catch(() => { if (alive) setState({ loading: false, score: null }); });
    return () => { alive = false; };
  }, []);

  const { loading, score } = state;
  const ready = score?.ready ?? 0;
  const total = score?.total ?? 0;
  const allLive = total > 0 && ready === total;

  return (
    <Link
      to="/admin/launch-rehearsal"
      data-testid="admin-launch-readiness-widget"
      data-state={allLive ? "ok" : "muted"}
      data-ready={ready}
      data-total={total}
      className={`group flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition-all hover:-translate-y-[1px] hover:shadow-sm ${
        allLive
          ? "border-primary/30 bg-primary/5"
          : "border-neutral-200 bg-white"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl ${
            allLive ? "bg-white text-primary-hover" : "bg-primary/10 text-primary-hover"
          }`}
        >
          {loading ? (
            <CircleNotch size={12} weight="bold" className="animate-spin" />
          ) : (
            <Confetti size={12} weight="duotone" />
          )}
        </span>
        <div className="min-w-0">
          <p
            data-testid="admin-launch-readiness-widget-label"
            className="truncate text-[12px] font-semibold text-neutral-900"
          >
            {loading
              ? "Checking launch readiness…"
              : total === 0
                ? "Open the launch rehearsal"
                : allLive
                  ? `Launch ready — all ${total} live signals confirmed`
                  : `Launch rehearsal: ${ready} of ${total} live signals confirmed`}
          </p>
        </div>
      </div>
      <ArrowRight
        size={11}
        weight="bold"
        className="flex-shrink-0 text-neutral-400 transition group-hover:translate-x-0.5 group-hover:text-primary-hover"
      />
    </Link>
  );
}
